/** @jsx jsx */

import { useState, useEffect, cloneElement } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/UI/Modal';
import { ThemeProvider } from 'emotion-theming';
import { theme } from 'theme';
import getStore from 'services/getStore';
import getTopics from 'services/apiHelpers/getTopics';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import { getButtonFocus } from 'styles/variables';
import { clearLocationHistory } from 'services/clearLocationHistory';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import renderBlock from 'services/apiHelpers/renderBlock';
import EditableText from 'components/lib/EditableText';
import EditableBtn from 'components/lib/EditableBtn';
import Image from 'components/lib/Image';
import bookmark_graphic from 'assets/Character C.png';
import pubSub from 'services/pubSub';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlockProps from 'services/apiHelpers/setBlockProps';
//
import isAndroidSamsung from 'services/isAndroidSamsung';
import isiOS from 'services/isiOS';
//
const store = getStore();
const breakpoint = 540;

const Button = props => {
  const { style, onClick, children } = props;

  return (
    <button
      css={css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: 'none',
        outline: 'none',
        width: 197,
        height: 46,
        margin: '20px 0 0 0',
        padding: 20,
        borderRadius: '9999px',
        background: '#19D1CF',
        fontSize: 14,
        color: '#fff',
        cursor: 'pointer',

        transition: 'opacity 0.2s',

        ':focus': {
          boxShadow: getButtonFocus('#060709').replace('box-shadow: ', ''),
        },

        ':hover': {
          opacity: 0.8,
        },

        ...style,
      })}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const Bookmark = props => {
  const { updateBookmarkState } = props;
  const [bookmark, setBookmark] = useState<string | null>(
    // (!window.dev() && 'true') ||
    // 'true' || null
    null
  );

  const [showLoadingBookmark, setShowLoadingBookmark] = useState<boolean>(
    false
  );

  const {
    textColor,
    buttonBackgroundColor,
    buttonTextColor,
    fontFamily,
  } = store.getState().theme;

  const { bookmarkLocation, bookmarkLoaded } = useSelector((state: State) => {
    return {
      bookmarkLocation: state.bookmarkLocation,
      bookmarkLoaded: state.bookmarkLoaded,
    };
  });

  const bookmarkBlock = useSelector((state: State) => {
    return Object.values(state.blocks).find(block => {
      return block?.type === 'Bookmark';
    });
  });

  let renderedBookmarkBlock;
  if (bookmarkBlock) {
    renderedBookmarkBlock = renderBlock(bookmarkBlock, {
      Image,
      Text: EditableText,
      EditableButton: EditableBtn,
    });
  }

  const [img, txt, noBtn, yesBtn] =
    renderedBookmarkBlock?.props?.children || [];

  const hasTextContent = txt?.props?.content ? 'true' : 'false';

  const resetAriaAndOverflowY = () => {
    const tick = setInterval(() => {
      const root = document.querySelector('#root')!;

      root.setAttribute('aria-hidden', 'false');
      document.body.style.overflowY = 'auto';

      if (document.body.style.overflowY === 'auto') {
        clearInterval(tick);
      }
    }, 100);
  };

  useEffect(() => {
    if (hasTextContent === 'false') {
      requestAnimationFrame(() => {
        setBlockProps(getBlockById(txt.props.id), {
          content:
            '<p data-type="paragraph">Would you like to return to where you left off?</p>',
        });
      });
    }
  }, []);

  useEffect(() => {
    if (window.dev && window.dev()) {
      return;
    }

    if (!bookmarkLocation || bookmarkLocation === 'null') {
      return;
    }

    console.log('bookmark prevents body from scrolling');

    const root = document.querySelector('#root')!;

    const tick = setInterval(() => {
      const ariaHidden = root.getAttribute('aria-hidden');

      if (ariaHidden === 'false') {
        root.setAttribute('aria-hidden', 'true');

        // run the check for mobile device and don't hide the overflow
        // why ? this is to prevent what SEEMS to be a very rare timing issue
        // brought to attention with an etrainu module that is ONLY an assessment but with bookmarking
        // tested with client and working to solve the problem. but would like to investigate more
        //
        // if (!(isAndroidSamsung || isiOS)) {
        //   document.body.style.overflowY = 'hidden';
        // }

        document.body.style.overflowY = 'hidden';

        clearInterval(tick);
      }
    }, 1000 / 60);

    return () => {
      clearInterval(tick);
    };
  }, [bookmarkLocation]);

  useEffect(() => {
    const location = bookmarkLocation;

    if (!location || location === 'null') {
      return;
    }

    // testing this as 1/1
    // but need to calculate what the actual first topic / interaction is

    const firstTopic: any = getTopics()[0];
    const firstInteraction: any = getInteractionsInTopic(firstTopic)[0];

    if (!firstTopic || !firstInteraction) {
      return;
    }

    const firstTopicSId = firstTopic.props.sId || firstTopic.id;
    const firstInteractionSId =
      firstInteraction.props.sId || firstInteraction.id;

    if (
      location &&
      location !== '' &&
      location !== `${firstTopicSId}/${firstInteractionSId}`
    ) {
      //

      setBookmark(location);
    }
  }, [bookmarkLoaded, bookmarkLocation]);

  useEffect(() => {
    // testing this out
    if (!bookmark) {
      updateBookmarkState(0);
      return;
    }

    updateBookmarkState(1);
  }, [bookmark]);

  useEffect(() => {
    if (!window.dev()) {
      return;
    }

    const pS = pubSub.subscribe('SHOW_BOOKMARK', ({ showBookmark }) => {
      setBookmark(showBookmark ? 'true' : null);
    });

    return () => {
      pS();
    };
  }, []);

  useEffect(() => {
    if (!bookmark) {
      return;
    }

    const pS = pubSub.subscribe('MODULE_NAVIGATION', ({ navToHash }) => {
      // i'm ' turning this off ' for the moment to get a better idea
      // of the blocking that happens when we attempt to show the loading spinner
      // from this event
      //
      // if (navToHash === 'starts') {
      //   setShowLoadingBookmark(true);
      //   return;
      // }

      if (navToHash === 'ends') {
        requestAnimationFrame(() => {
          clearLocationHistory();
          setBookmark(null);
          setShowLoadingBookmark(false);
          //
          updateBookmarkState(2);

          resetAriaAndOverflowY();
        });
      }
    });

    return () => {
      pS();
      //
    };
  }, [bookmark]); //

  const noBtnOnClick = () => {
    setCurrentTopic(getTopics()[0].id);
    setBookmark(null);
    setShowLoadingBookmark(false);
    //
    updateBookmarkState(2);

    resetAriaAndOverflowY();
  };

  const yesBtnOnClick = () => {
    if (bookmark) {
      // this seems to work to display the spinner
      //
      setShowLoadingBookmark(true);

      requestAnimationFrame(() => {
        window.location.hash = bookmark;

        // we do this to clear these values which are set when navigating
        // nav to bookmark is its own special case where we do not want / need them
        // and always want to nav to the logical prev section or topic
        setTimeout(() => {
          window.persistHistory = false;
          window.locationHistory = null;
        }, 800);
      });
    }
  };

  return !!bookmark ? (
    <ThemeProvider theme={theme}>
      <Modal
        key={hasTextContent}
        disableNullSpaceClick
        useFader={window.dev() ? true : false}
        onClose={() => {
          setBookmark(null);
          //
          updateBookmarkState(2);

          resetAriaAndOverflowY();
        }}
      >
        <div
          id="chameleon-bookmark"
          css={css({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: '540px',
            padding: '100px 0',
            pointerEvents: showLoadingBookmark ? 'none' : 'auto',
          })}
        >
          {img ? (
            cloneElement(img, {
              placeholder: bookmark_graphic,
            })
          ) : (
            <img
              src={bookmark_graphic}
              css={css({
                width: '100%',
                maxWidth: 540,
                margin: '0 0 30px 0',
              })}
            />
          )}

          <div
            css={css({
              width: '100%',
              margin: '10px 0 0 0',
              fontWeight: 400,
              fontSize: 18,
              textAlign: 'center',

              [`@media (max-width: ${breakpoint}px)`]: {
                padding: '0 20px',
              },
            })}
          >
            {txt ? (
              txt
            ) : (
              <p
                className="chameleon-text"
                css={css({
                  fontWeight: 400,
                  fontSize: 18,
                  textAlign: 'center',
                })}
              >
                Would you like to return to where you left off?
              </p>
            )}
          </div>

          <div
            css={css({
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
              margin: '30px 0',
              button: {
                minWidth: 197,
                margin: '0 20px',
                [`@media (max-width: ${breakpoint}px)`]: {
                  ':first-child': {
                    marginBottom: 20,
                  },
                },
              },

              'div [class*="GenericButton"]': {
                //

                left: '10px',
                top: '-10px',
                width: 'calc(100% - 20px)',
              },
            })}
          >
            {noBtn ? (
              cloneElement(noBtn, {
                onClick() {
                  noBtnOnClick();
                },
              })
            ) : (
              <Button
                style={{
                  background: '#fff',
                  outline: '1px solid #060709',
                  color: '#060709',
                  opacity: showLoadingBookmark ? 0.3 : 1,
                  transition: 'opacity 0.2s',
                }}
                onClick={() => {
                  noBtnOnClick();
                }}
              >
                No
              </Button>
            )}
            {/*  */}
            {yesBtn ? (
              cloneElement(yesBtn, {
                onClick() {
                  yesBtnOnClick();
                },
              })
            ) : (
              <Button
                style={{
                  position: 'relative',
                  color: '#fff',
                  background: '#060709',
                  fontFamily,
                  boxShadow:
                    '-16px -16px 27px rgba(255, 255, 255, 0.5), 16px 16px 27px rgba(37, 59, 176, 0.14393)',
                }}
                onClick={() => {
                  yesBtnOnClick();
                }}
              >
                Yes{' '}
                <div
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: 46 / 2 - 30 / 2,
                  }}
                >
                  {showLoadingBookmark && <LoadingSpinner size={30} />}
                </div>
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </ThemeProvider>
  ) : null;
};

export default Bookmark;
